/**
 * Contract tab — inside /doserne/profil.
 *
 * Shows the user's employment contract PDF (from Cloudinary) and, if not yet
 * signed, presents a dual signing UI: canvas draw + typed name. The backend
 * regenerates the PDF with the signature page appended.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Loader2, PenLine, CheckCircle2, Download, Type, Brush } from "lucide-react";
import SignaturePad from "../apps/shared/SignaturePad";
import PdfViewerModal from "./PdfViewerModal";
import LangFlag from "./LangFlag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_NAMES = { no: "Norsk", ro: "Română", en: "English", pl: "Polski" };

export default function ContractTab({ authHeader }) {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("drawn");   // "drawn" | "typed"
  const [drawn, setDrawn] = useState(null);
  const [typed, setTyped] = useState("");
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfLang, setPdfLang] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/me/contract`, authHeader());
      setContract(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke laste kontrakt");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const sign = async () => {
    if (!ack) { toast.error("Bekreft aksepten før du signerer"); return; }
    if (mode === "drawn" && !drawn) { toast.error("Tegn signaturen din"); return; }
    if (mode === "typed" && typed.trim().length < 2) { toast.error("Skriv fullt navn"); return; }
    setSaving(true);
    try {
      const payload = mode === "drawn"
        ? { signature: drawn, signature_type: "drawn" }
        : { signature_text: typed.trim(), signature_type: "typed" };
      await axios.post(`${API}/doserne/me/contract/sign`, payload, authHeader());
      toast.success("Kontrakt signert · Contract signed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke signere");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  if (!contract?.has_contract) {
    return (
      <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center" data-testid="contract-none">
        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <h3 className="text-lg font-black text-slate-900 mb-1">Ingen kontrakt tilgjengelig</h3>
        <p className="text-sm text-slate-500 max-w-sm mx-auto">
          Administratoren har ikke lastet opp en arbeidskontrakt for kontoen din ennå.
          Ta kontakt med kontoret hvis du forventer en.
        </p>
      </div>
    );
  }

  const signed = !!contract.signed_at;
  const viewUrl = contract.signed_pdf_url || contract.contract_pdf_url;
  const signedLangs = contract.signed_langs || [];

  return (
    <div className="space-y-4" data-testid="contract-tab">
      {/* Contract card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5" style={{ color: "#22d3ee" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Arbeidskontrakt</div>
            <div className="text-base font-black text-slate-900">
              {signed ? "Din signerte kontrakt" : "Kontrakt til gjennomgang og signering"}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {signed
                ? <>Signert {new Date(contract.signed_at).toLocaleString("no-NO")}</>
                : "Åpne PDF-en, les den, og signer nedenfor for å bekrefte aksepten."}
            </div>
          </div>
          {signed && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase tracking-widest" data-testid="contract-signed-badge">
              <CheckCircle2 className="w-3 h-3" /> Signert
            </span>
          )}
        </div>

        <div className="mt-4">
          {signed && signedLangs.length > 0 ? (
            <>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-2">
                Åpne / last ned · Deschide / descarcă
              </div>
              <div className="flex flex-wrap gap-2" data-testid="contract-lang-list">
                {signedLangs.map((lg) => (
                  <button
                    key={lg}
                    type="button"
                    onClick={() => setPdfLang(lg)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold border-2 ${lg === contract.signed_lang ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700 bg-white hover:border-slate-400"}`}
                    data-testid={`contract-lang-${lg}`}
                  >
                    <LangFlag lang={lg} size={18} /> {LANG_NAMES[lg] || lg.toUpperCase()}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-wrap gap-2">
              <a href={viewUrl} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
                 data-testid="contract-view-btn">
                <FileText className="w-4 h-4" /> Åpne PDF
              </a>
              {signed && contract.signed_pdf_url && (
                <a href={contract.signed_pdf_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-400"
                   data-testid="contract-download-signed">
                  <Download className="w-4 h-4" /> Last ned signert versjon
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Signing block */}
      {!signed && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="contract-sign-block">
          <div className="flex items-center gap-2">
            <PenLine className="w-4 h-4 text-slate-500" />
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Signer digitalt</div>
          </div>

          {/* Mode switcher */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode("drawn")}
              className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold border-2 transition ${mode === "drawn" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
              data-testid="sig-mode-drawn"
            >
              <Brush className="w-4 h-4" /> Tegn signatur
            </button>
            <button
              type="button"
              onClick={() => setMode("typed")}
              className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold border-2 transition ${mode === "typed" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
              data-testid="sig-mode-typed"
            >
              <Type className="w-4 h-4" /> Skriv navnet ditt
            </button>
          </div>

          {mode === "drawn" ? (
            <SignaturePad value={drawn} onChange={setDrawn} height={160} label="Tegn her" />
          ) : (
            <div>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="Skriv fullt navn"
                className="w-full px-4 py-4 rounded-lg border-2 border-slate-200 text-slate-900 bg-white text-2xl focus:outline-none focus:border-slate-900"
                style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}
                data-testid="sig-typed-input"
              />
              <div className="mt-1 text-[11px] text-slate-500">Ved å skrive inn navnet ditt over, avgir du en juridisk gyldig digital signatur.</div>
            </div>
          )}

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                   className="mt-1 w-4 h-4 rounded border-slate-300"
                   data-testid="sig-ack" />
            <span className="text-sm text-slate-700 leading-snug">
              Jeg har lest og godtar kontrakten i sin helhet. Denne handlingen registreres med
              tidsstempel og IP-adresse, og en signaturside føyes til PDF-en.
            </span>
          </label>

          <button
            type="button"
            onClick={sign}
            disabled={saving || !ack || (mode === "drawn" ? !drawn : typed.trim().length < 2)}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-black uppercase tracking-widest bg-emerald-500 hover:bg-emerald-400 text-slate-900 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
            data-testid="sig-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Signer kontrakten
          </button>
        </div>
      )}

      {pdfLang && (
        <PdfViewerModal
          url={`${API}/doserne/me/contract/pdf?lang=${pdfLang}&signed=1`}
          authHeader={authHeader}
          filename={`Kontrakt_${pdfLang.toUpperCase()}.pdf`}
          title={`Kontrakt · ${LANG_NAMES[pdfLang] || pdfLang.toUpperCase()}`}
          labels={{ download: "Last ned / Print", close: "Lukk" }}
          onClose={() => setPdfLang(null)}
        />
      )}
    </div>
  );
}
